import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

interface UseDebouncedSearchInputOptions {
  /** What the owner has actually applied (the URL search param, typically). */
  committed: string | undefined;
  /** Receives the paused-on text, or `undefined` for an emptied box. */
  onCommit: (value: string | undefined) => void;
  delayMs?: number;
}

interface DebouncedSearchInput {
  value: string;
  onChange: (next: string) => void;
  /**
   * Empty the box and drop any pending commit. Call it from a Clear-filters
   * handler: when the committed search is already empty, clearing does not
   * change `committed`, so nothing else would stop the pending keystrokes from
   * landing a moment later.
   */
  reset: () => void;
}

/**
 * An eagerly-updating text box whose value is committed (to the URL, a saved
 * filter row, a query key…) only after the user pauses typing.
 *
 * The box shows `draft` on every keystroke; `committed` is what the owner has
 * actually applied. When the owner changes `committed` from outside — Clear
 * filters, browser back/forward, a saved-filter restore on landing — the draft
 * snaps to it and anything still waiting to commit is dropped, so a stale
 * keystroke can never resurrect a filter the user just cleared. The owner's
 * own commit landing is recognised and left alone, so typing that continues
 * past the debounce window is never truncated.
 *
 * @example
 * const search = useDebouncedSearchInput({
 *   committed: searchParams.q,
 *   onCommit: (value) => updateSearchParams({ q: value }, true),
 * });
 * <Input value={search.value} onChange={(e) => search.onChange(e.target.value)} />
 */
export function useDebouncedSearchInput({
  committed,
  onCommit,
  delayMs = DEFAULT_SEARCH_DEBOUNCE_MS,
}: UseDebouncedSearchInputOptions): DebouncedSearchInput {
  const committedText = committed ?? '';
  const [draft, setDraft] = useState(committedText);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The value of our most recent commit, until the owner reflects it back.
  const ownCommitRef = useRef<string | null>(null);
  // Always the latest `onCommit`, so a pending timer never calls a stale one.
  // Owners rebuild it whenever the URL changes, so this deliberately tracks
  // every render rather than depending on the function's identity, and it
  // refreshes during commit (layout effect) so a timer due right after a
  // rerender cannot still reach the previous closure.
  const onCommitRef = useRef(onCommit);
  useLayoutEffect(() => {
    onCommitRef.current = onCommit;
  });

  const cancelPending = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (ownCommitRef.current === committedText) {
      // Our own commit came back around; the draft already reads this way (or
      // has moved on to newer keystrokes that are still pending).
      ownCommitRef.current = null;
      return;
    }
    // An outside change supersedes whatever we last sent, so a later return to
    // that value must sync too rather than read as our own echo.
    ownCommitRef.current = null;
    cancelPending();
    setDraft(committedText);
  }, [committedText]);

  useEffect(() => cancelPending, []);

  const onChange = (next: string) => {
    setDraft(next);
    cancelPending();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      ownCommitRef.current = next;
      onCommitRef.current(next === '' ? undefined : next);
    }, delayMs);
  };

  const reset = () => {
    cancelPending();
    ownCommitRef.current = null;
    setDraft('');
  };

  return { value: draft, onChange, reset };
}
